import React, { useContext, createContext, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import jwtDecode from "jwt-decode";

export const AuthContext = createContext(null);

export type AuthContextValue = ReturnType<typeof useAuthContextValue>;

export type JWT = {
  /** E.g. "https://auth.decodable.co/" */
  iss?: string;
  /** The user ID from the IDP */
  sub?: string;
  /** E.g. ["https://api.decodable.co/", "https://decodable.us.auth0.com/userinfo"] */
  aud?: string[];
  /** Issued at time */
  iat?: number;
  /** Expiration time */
  exp?: number;
  /** Authorized party */
  azp?: string;
  /** Scope */
  scope?: string;
  /** The Auth0 org ID. Only present if the user is logged into a given org/account. */
  org_id?: string;
  /** Permissions granted to this user. */
  permissions: string[];
};

export function useAuthContextValue() {
  const [accessToken, setAccessToken] = useState(null);
  const [jwt, setJwt] = useState<JWT>(null);

  const {
    getAccessTokenSilently,
    isLoading,
    isAuthenticated,
    user,
    loginWithRedirect,
    logout,
    error,
  } = useAuth0();

  useEffect(() => {
    let cancel = false;

    if (isLoading || !isAuthenticated) {
      return;
    }

    (async function () {
      try {
        const accessToken = await getAccessTokenSilently();
        if (!cancel) {
          setAccessToken(accessToken);
          setJwt(jwtDecode(accessToken));
        }
      } catch (ex) {
        console.warn("Could not retrieve token", ex);
        throw ex;
      }
    })();

    return () => {
      cancel = true;
    };
  }, [isLoading, isAuthenticated]);

  return {
    accessToken,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    error,
    jwt,
    loginWithRedirect,
    logout: (...params: Parameters<typeof logout>) => {
      sessionStorage.clear();
      logout(...params);
    },
    user,
  };
}

export function AuthProvider({ children }) {
  const ctx = useAuthContextValue();
  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
}

export function useAuth(): ReturnType<typeof useAuthContextValue> {
  const ctx = useContext(AuthContext);
  if (ctx == null) {
    throw new Error(
      "useAuth may only be used in the context of an AuthProvider!"
    );
  }
  return ctx;
}
